import { Icon } from '@99designs/common/components/Icon';
import { Tooltip } from '@99designs/common/components/Tooltip';
import { Currency, CurrencySymbol, toSubUnit, toUnit } from 'common/utils/currency';
import React, { Component } from 'react';
import { FormattedNumber } from 'react-intl';
import ReactSlider from 'react-slider';
import getBudgetStepsForCurrency from './getBudgetStepsForCurrency';

export const enum BudgetType {
    TEXT_BOX_FIELD = 'textBoxField',
    SLIDER_FIELD = 'sliderField',
}

interface Category {
    title: string;
    priceRange: number[];
}

const PriceGuide: React.FC<{
    category: Category;
    currency: Currency;
}> = ({ category, currency }) => (
    <p className="price-guide">
        {__('Price guide:')}{' '}
        <FormattedNumber
            value={toUnit(category.priceRange[0], currency as Currency)}
            style="currency"
            currency={currency}
            minimumFractionDigits={0}
        />
        {' – '}
        <FormattedNumber
            value={toUnit(category.priceRange[1], currency as Currency)}
            style="currency"
            currency={currency}
            minimumFractionDigits={0}
        />
        <Tooltip
            position={'up-left'}
            tooltip={
                <div style={{ maxWidth: 360 }}>
                    {__(
                        'Based on the prices that designers typically quote for projects in this category.'
                    )}
                </div>
            }
        >
            <span className="stock-example-trigger">
                <Icon type={'question-mark-1'} variations={['offset-left']} />
            </span>
        </Tooltip>
    </p>
);

export interface Props {
    budget: number | null;
    onSliderChange: (budget: number, inputType: string) => void;
    currency: Currency;
    category: Category | null;
}

export function getClosestIndex(stepMapping: number[], maxValue: number) {
    let current = 0;
    let currentStep = 0;
    let minimum = 0;

    for (let i = 0; i < stepMapping.length; i++) {
        const step = stepMapping[i];

        current = Math.abs(step - maxValue);
        currentStep = stepMapping.indexOf(step);

        if (i === 0) {
            minimum = current;
        }

        if (current > minimum) {
            currentStep = currentStep - 1;
            break;
        }

        minimum = current;
    }

    return currentStep;
}

export default class BudgetSlider extends Component<Props> {
    stepMapping: number[];

    constructor(props: Props) {
        super(props);
        this.stepMapping = getBudgetStepsForCurrency(props.currency).map(value =>
            toSubUnit(value, props.currency)
        );
    }

    onChange = (value: number, inputType: string) => {
        this.props.onSliderChange(
            value < this.stepMapping.length ? this.stepMapping[value] : 0,
            inputType
        );
    };

    getSliderValue() {
        return this.props.budget
            ? getClosestIndex(this.stepMapping, this.props.budget)
            : this.stepMapping.length;
    }

    shouldShowPlaceholder() {
        return this.props.budget === 0 || this.props.budget === null;
    }

    render() {
        const sliderClasses = 'slider slider--budget slider--initialised';
        const { category, currency } = this.props;
        return (
            <div className="budget-slider">
                <div className="budget-slider__input">
                    <div className="budget-input">
                        <div className="budget-input__input">
                            <div className="budget-input__input__currency">
                                {CurrencySymbol[currency]}
                            </div>
                            <input
                                type="number"
                                min={0}
                                max={Number.MAX_SAFE_INTEGER}
                                className="input-text input-width--x-short"
                                value={
                                    this.shouldShowPlaceholder()
                                        ? ''
                                        : toUnit(this.props.budget || 0, currency)
                                }
                                placeholder={__('Enter amount')}
                                onChange={({ target }) => {
                                    this.props.onSliderChange(
                                        toSubUnit(parseInt(target.value), currency),
                                        BudgetType.TEXT_BOX_FIELD
                                    );
                                }}
                            />
                        </div>
                    </div>
                </div>
                {category && category.priceRange.length > 0 && (
                    <div className="budget-slider__price-guide">
                        <PriceGuide category={category} currency={currency} />
                    </div>
                )}
                <div className="budget-slider__slider">
                    <ReactSlider
                        value={[this.getSliderValue()]}
                        max={this.stepMapping.length}
                        handleClassName="slider__handle"
                        handleActiveClassName="slider__handle--active"
                        className={sliderClasses}
                        onChange={e => this.onChange(e as number, BudgetType.SLIDER_FIELD)}
                        withBars={true}
                        orientation="horizontal"
                    />
                </div>
            </div>
        );
    }
}
